import {
  EContractStatus,
  EDashboardSortFields,
  EMeasurementStatus,
} from "api/enums";
import {
  IDashboardFilter,
  IMeasurementTotalsPerMonthData,
} from "api/interfaces";
import ContractModel from "api/models/ContractModel";
import MeasurementService from "api/services/MeasurementService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { compareStrings, includesText } from "utils";
import { dividedBy, subtract, sum } from "utils/DecimalPrecision";
import ContractListStore from "./Contract/ContractListStore";
import MessageStore from "./MessageStore";
import UserStore from "./UserStore";

class Store {
  public open = false;
  public contractsCount = 0;
  public contractsByStatus: { [status: string]: number } = {};
  public contractsBilling = {
    total: 0,
    measuredTotal: 0,
    toBeMeasuredTotal: 0,
    measuredPercentual: 0,
    toBeMeasuredPercentual: 0,
  };
  public measurementsPerMonth: any[] = [];
  public measurementsTotalsByStatus: { [status: string]: number } = {};
  public loadingMeasurePerMonth = false;
  public filteredContracts: ContractModel[] = [];
  public filters: IDashboardFilter = {
    periodInitialDate: null,
    periodFinalDate: null,
    selectedStatus: [],
  };
  public selectedContractsUids: string[] = [];
  public inputSearch: string = "";
  public sortField: string = "";
  public sortOrder: string = "ASC";

  constructor() {
    makeAutoObservable(this);
  }

  public reset() {
    this.open = false;
    this.contractsCount = 0;
    this.contractsByStatus = {};
    this.contractsBilling = {
      total: 0,
      measuredTotal: 0,
      toBeMeasuredTotal: 0,
      measuredPercentual: 0,
      toBeMeasuredPercentual: 0,
    };
    this.measurementsPerMonth = [];
    this.measurementsTotalsByStatus = {};
    this.loadingMeasurePerMonth = false;
    this.filteredContracts = [];
    this.sortField = "";
    this.sortOrder = "ASC";
    this.resetFilters();
  }

  public resetFilters() {
    this.inputSearch = "";
    this.selectedContractsUids = [];
    this.filters = {
      periodInitialDate: null,
      periodFinalDate: null,
      selectedStatus: this.initializeFilterSelectedStatus(),
    };
  }

  private initializeFilterSelectedStatus() {
    return Object.values(EContractStatus).filter(
      (stt) => stt !== EContractStatus.ARQUIVADO
    );
  }

  public refreshContractsData() {
    if (!this.open) return;

    const contracts = this.filterContracts(true);
    if (!contracts || !contracts.length) return;

    this.setContractsCount(contracts.length);
    this.setContractsByStatus(contracts);
    this.setContractsBilling(contracts);
    this.fetchMeasurementsPerMonthData(contracts);
  }

  private setContractsCount(count: number) {
    this.contractsCount = count;
  }

  private setContractsByStatus(contracts: ContractModel[]) {
    const obj = {};
    contracts.forEach((contract) => {
      if (!obj[contract.status]) obj[contract.status] = 0;
      obj[contract.status]++;
    });
    this.contractsByStatus = obj;
  }

  private setContractsBilling(contracts: ContractModel[]) {
    const billing = { ...this.contractsBilling };
    let total = 0;
    let measuredValue = 0;

    contracts.forEach((contract) => {
      total = sum(total, contract.total);
      measuredValue = sum(measuredValue, contract.measuredTotal);
    });

    billing.total = total;
    billing.measuredTotal = measuredValue;
    billing.toBeMeasuredTotal = subtract(total, measuredValue);
    billing.measuredPercentual = dividedBy(measuredValue, total);
    billing.toBeMeasuredPercentual = dividedBy(
      billing.toBeMeasuredTotal,
      total
    );

    this.contractsBilling = billing;
  }

  public setFilterPeriodDateRange(range: {
    start: Date | null;
    end: Date | null;
  }) {
    const newFilters = { ...this.filters };
    newFilters.periodInitialDate = range.start;
    newFilters.periodFinalDate = range.end;
    this.filters = newFilters;
  }

  public setFilterStatus(status: string) {
    const newFilters = { ...this.filters };
    if (newFilters.selectedStatus.includes(status)) {
      newFilters.selectedStatus = newFilters.selectedStatus.filter(
        (stt) => stt !== status
      );
    } else {
      newFilters.selectedStatus.push(status);
    }
    this.filters = newFilters;
  }

  public setInputSearch(term: string) {
    this.inputSearch = term;
  }

  public setSortField(field: EDashboardSortFields) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
    } else {
      this.sortOrder = "ASC";
    }
    this.sortField = field;
  }

  public setOpen(open: boolean) {
    this.open = open;
  }

  public addSelectedContractUid(contractUid: string) {
    if (this.selectedContractsUids.includes(contractUid)) return;
    this.selectedContractsUids.push(contractUid);
  }

  public removeSelectedContractUid(contractUid: string) {
    this.selectedContractsUids = this.selectedContractsUids.filter(
      (uid) => uid !== contractUid
    );
  }

  public fetchMeasurementsPerMonthData(contracts: ContractModel[]) {
    this.setLoadingMeasurePerMonth(true);
    const uids = contracts.map((contract) => contract.uid);
    MeasurementService.fetchMeasurementsTotalsPerMonth(uids)
      .then((result) => {
        this.measurementsPerMonth = this.formatData(result);
        this.measurementsTotalsByStatus =
          this.getMeasurementsTotalsByStatus(result);
      })
      .catch((error) => {
        MessageStore.error(lang("dashboard.loadError", error.message));
      })
      .finally(() => this.setLoadingMeasurePerMonth(false));
  }

  public filterContracts(filterWithSelected = false) {
    const contracts = ContractListStore.contracts;
    const searchTerm = this.inputSearch;
    const selectedStatus = this.filters.selectedStatus;
    const startDate = this.filters.periodInitialDate;
    const finalDate = this.filters.periodFinalDate;

    if (filterWithSelected && this.selectedContractsUids.length) {
      return contracts.filter((contract) =>
        this.selectedContractsUids.includes(contract.uid)
      );
    }

    let filteredContracts = contracts.filter((contract) =>
      selectedStatus.includes(contract.status)
    );

    if (startDate && finalDate) {
      finalDate.setHours(23, 59, 59);
      filteredContracts = filteredContracts.filter(
        (contract) =>
          contract.createdAt.getTime() >= startDate.getTime() &&
          contract.createdAt.getTime() <= finalDate.getTime()
      );
    }

    if (searchTerm) {
      filteredContracts = filteredContracts.filter(
        (contract) =>
          includesText(contract.name, searchTerm) ||
          includesText(contract.development.name, searchTerm) ||
          includesText(contract.development.customer, searchTerm) ||
          includesText(contract.development.local, searchTerm)
      );
    }

    return filteredContracts;
  }

  public sortContracts(contracts: ContractModel[]) {
    const field = this.sortField;
    return contracts.sort((a, b) => {
      if (this.sortOrder === "ASC") {
        switch (field) {
          case EDashboardSortFields.PROJECT:
            return compareStrings(a.development.name, b.development.name);
          case EDashboardSortFields.CLIENT:
            return compareStrings(
              a.development.customer,
              b.development.customer
            );
          case EDashboardSortFields.LOCATION:
            return compareStrings(a.development.local, b.development.local);
          case EDashboardSortFields.CREATED_DATE:
            return a.createdAt.getTime() - b.createdAt.getTime();
          default:
            return compareStrings(a.name, b.name);
        }
      }

      switch (field) {
        case EDashboardSortFields.PROJECT:
          return compareStrings(b.development.name, a.development.name);
        case EDashboardSortFields.CLIENT:
          return compareStrings(b.development.customer, a.development.customer);
        case EDashboardSortFields.LOCATION:
          return compareStrings(b.development.local, a.development.local);
        case EDashboardSortFields.CREATED_DATE:
          return b.createdAt.getTime() - a.createdAt.getTime();
        default:
          return compareStrings(b.name, a.name);
      }
    });
  }

  public filterAndSetFilteredContracts() {
    let contracts = this.filterContracts();
    contracts = this.sortContracts(contracts);
    this.filteredContracts = contracts;
  }

  public filterSelectedContracts(){
    const filteredUids = this.filteredContracts.map(contract => contract.uid);
    this.selectedContractsUids = this.selectedContractsUids.filter(uid => filteredUids.includes(uid));
  }

  public setFiltersWithUserData() {
    const user = UserStore.currentUser;
    if (!user) return;

    const filters = {
      periodInitialDate: user.dashboardFilters.periodInitialDate || null,
      periodFinalDate: user.dashboardFilters.periodFinalDate || null,
      selectedStatus: user.dashboardFilters.selectedStatus || [],
    };

    if (!filters.selectedStatus.length) {
      filters.selectedStatus = this.initializeFilterSelectedStatus();
    }

    this.filters = filters;
  }

  private setLoadingMeasurePerMonth(loading: boolean) {
    this.loadingMeasurePerMonth = loading;
  }

  private formatData(data: IMeasurementTotalsPerMonthData) {
    const statusList = Object.values(EMeasurementStatus);
    return data.monthsRange.map((month) => {
      const currentData = data.result[month] || {};
      const formattedData: any = { name: month };

      statusList.forEach((status) => {
        const key = lang(`dashboard.measureStatus.${status}`);
        formattedData[key] = currentData[status] || 0;
      });

      return formattedData;
    });
  }

  private getMeasurementsTotalsByStatus(data: IMeasurementTotalsPerMonthData) {
    const obj = {};
    for (const totals of Object.values(data.result)) {
      for (const status of Object.keys(totals)) {
        if (!obj[status]) obj[status] = 0;
        obj[status] += totals[status] || 0;
      }
    }
    return obj;
  }
}

const DashboardStore = new Store();
export default DashboardStore;
